<script setup lang="ts">
import { ITicketPurchased } from '@/features/tickets/types'
import { useTicketsForEventQuery } from '@/features/tickets/api/queries'
import { computed, ref } from 'vue'
import BKButton from '@/components/AppShared/BKButton.vue'
import { centsToDollars } from '@/services/currency'
import { ModalWrapper } from '@/shared/ui/modals'
import ChangeTicketsInterface from '@/features/tickets/ui/ChangeTicketsInterface.vue'

const props = defineProps<{
  currentTicket: ITicketPurchased
  eventId: number
  isAdmin?: boolean
}>()

const { data: tiers, isLoading: isLoadingTickets, isError: isTicketsError } = useTicketsForEventQuery(props.eventId)

const selectedTierId = ref<number | null>(props.currentTicket.tierId)
const isTierChanged = computed(() => {
  return selectedTierId.value !== null && selectedTierId.value !== props.currentTicket.tierId
})
const getPriceDifferenceToCurrentTier = (tierId: number) => {
  if (!tiers.value) return 0
  const selectedTier = tiers.value.tiers.find((tier) => tier.id === tierId)
  if (!selectedTier) return 0
  return (selectedTier.priceWithDiscount || selectedTier.price) - props.currentTicket.cost
}
const emit = defineEmits<{
  (e: 'close'): void
}>()

const isShowPurchaseModal = ref(false)

const handleChangeTier = () => {
  isShowPurchaseModal.value = true
}

const handleTierChanged = () => {
  isShowPurchaseModal.value = false
  emit('close')
}
</script>

<template>
  <div
    class="change-tier-interface"
    :data-hidden="isShowPurchaseModal"
  >
    <div class="content">
      <p class="content-header">Ticket type</p>
      <div
        v-if="isLoadingTickets"
        class="tickets-loader"
      >
        <v-skeleton-loader type="heading" />
        <v-skeleton-loader type="text" />
        <v-skeleton-loader type="text" />
      </div>
      <div v-else-if="isTicketsError">
        <v-alert type="error"> Error loading available ticket tiers. Please try again later. </v-alert>
      </div>
      <v-radio-group
        v-else-if="tiers"
        v-model="selectedTierId"
        class="tiers-list"
      >
        <template v-for="tier in tiers.tiers">
          <!-- eslint-disable-next-line vuejs-accessibility/label-has-for -->
          <label
            v-if="getPriceDifferenceToCurrentTier(tier.id) >= 0"
            :key="tier.id"
            class="tier-item"
          >
            <span class="tier-title">
              {{ tier.title }}
              <span
                v-if="tier.id === currentTicket.tierId"
                class="current-tier-indicator"
              >
                {{ ' (current)' }}
              </span>
            </span>
            <span class="tier-description">
              {{ tier.description }}
            </span>
            <span
              v-if="tier.discountTitle && !(tier.id === currentTicket.tierId)"
              class="tier-discount"
            >
              Applied discounts: {{ tier.discountTitle }}
              <span class="tier-discount-value">
                {{ `-${centsToDollars(tier.price - (tier.priceWithDiscount || tier.price))}` }}
              </span>
            </span>
            <span class="tier-price">
              {{ centsToDollars(tier.priceWithDiscount || tier.price) }}
              <span
                v-if="tier.id !== currentTicket.tierId"
                class="tier-price-difference"
                :data-positive="getPriceDifferenceToCurrentTier(tier.id) >= 0"
              >
                {{
                  getPriceDifferenceToCurrentTier(tier.id) > 0
                    ? `+${centsToDollars(getPriceDifferenceToCurrentTier(tier.id))}`
                    : 'No change'
                }}
              </span>
            </span>
            <v-radio
              :value="tier.id"
              class="tier-radio"
              color="var(--color-blue-800)"
            />
          </label>
        </template>
      </v-radio-group>
    </div>
    <div class="actions">
      <BKButton
        class="action-button"
        outlined
        @click="$emit('close')"
      >
        Cancel
      </BKButton>
      <BKButton
        class="action-button"
        :disabled="!isTierChanged"
        white-text
        @click="handleChangeTier"
      >
        Continue to Checkout
      </BKButton>
    </div>

    <ModalWrapper
      :is-show="isShowPurchaseModal"
      modal-width="904px"
      @update:isShow="isShowPurchaseModal = $event"
    >
      <ChangeTicketsInterface
        v-if="isShowPurchaseModal && !!selectedTierId"
        :event-id="eventId"
        :ticket-id="currentTicket.id"
        :new-tier-id="Number(selectedTierId)"
        :is-admin="isAdmin"
        @close="isShowPurchaseModal = false"
        @tier-changed="handleTierChanged"
      />
    </ModalWrapper>
  </div>
</template>

<style scoped lang="scss">
.change-tier-interface {
  display: flex;
  flex-direction: column;
  gap: 40px;
  justify-content: space-between;
  height: 100%;

  :global(.v-dialog:has(.change-tier-interface[data-hidden='true'])) {
    display: none;
  }

  p {
    margin: 0;
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .content-header {
    font-size: 14px;
    line-height: 20px;
    color: var(--color-grey-300);
  }

  .tickets-loader {
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    height: 90px;
    border: 1px solid var(--color-grey-500);
    border-radius: 6px;
    cursor: progress;

    :deep(.v-skeleton-loader__text) {
      margin-bottom: 0;
    }
  }

  .tiers-list {
    padding: 0;
    margin: 0;

    :deep(.v-input--radio-group__input) {
      gap: 16px;
    }

    .tier-item {
      display: grid;
      grid-template:
        'title radio'
        'description description'
        'discount discount'
        'price price' / 1fr auto;
      gap: 4px;
      padding: 12px;
      border: 1px solid var(--color-grey-500);
      border-radius: 6px;
      cursor: pointer;

      &:hover {
        background-color: var(--color-grey-150);
      }
    }

    .tier-title {
      grid-area: title;
      font-weight: 600;
      font-size: 14px;
      line-height: 1.3;
      color: var(--color-dark-800);
    }

    .current-tier-indicator {
      color: var(--color-gold-900);
    }

    .tier-description {
      grid-area: description;
      font-size: 14px;
      line-height: 20px;
      color: var(--color-grey-300);
    }

    .tier-price {
      grid-area: price;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      color: var(--color-grey-300);

      .tier-price-difference {
        margin-left: 10px;
        color: var(--rasberry-red);

        &[data-positive='true'] {
          color: var(--color-green);
        }
      }
    }

    .tier-discount {
      grid-area: discount;
      font-weight: 600;
      font-size: 14px;
      line-height: 20px;
      color: var(--color-grey-300);

      .tier-discount-value {
        margin-left: 10px;
      }
    }

    .tier-radio {
      grid-area: radio;
      align-self: start;
    }
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;

    .action-button {
      flex: 1 1 0;
      min-width: fit-content;
    }
  }
}
</style>
